import {
  bind,
  createStableId,
  define,
  getHost,
  html,
  onCleanup,
  onEvent,
  onMounted,
  prop,
  ref,
  useField,
  useSlots,
} from '@vielzeug/ore';
import { computed, signal, watch } from '@vielzeug/ripple';
import { createErrorHelperState, createSliderControl } from '../../core';
import { disablableBundle, SLIDER_SIZE_PRESET, sizableBundle, themableBundle } from '../../shared';
import { colorThemeMixin, disabledStateMixin, reducedMotionMixin, sizeVariantMixin } from '../../styles';
import type { ComponentSize, ThemeColor } from '../../types';
import { defineFieldValue, dispatchNativeFieldEvent, setFieldValue } from '../shared/native-field-event';
import { renderHelperRegion } from '../shared/templates';
import componentStyles from './slider.css?inline';

const guard =
  <E extends Event = Event>(condition: () => unknown, handler: (e: E) => void): ((e: E) => void) =>
  (e) => {
    if (condition()) handler(e);
  };

/** Slider component properties */

export type OreSliderEvents = {
  change: Event;
  input: Event;
};

export type OreSliderProps = {
  /** Theme color */
  color?: ThemeColor;
  /** Disable interaction */
  disabled?: boolean;
  /** Error message — marks the field as invalid (fallback when the `error` slot is empty) */
  error?: string;
  /** Range mode: lower bound */
  from?: number | string;
  /** Range mode a11y label for the start thumb (e.g. "$20") */
  'from-value-text'?: string;
  /** Stretch to full width of container */
  fullwidth?: boolean;
  /** Helper text displayed below the slider (fallback when the `helper` slot is empty) */
  helper?: string;
  /** Maximum value */
  max?: number | string;
  /** Minimum value */
  min?: number | string;
  /** Single-value mode: form field name */
  name?: string;
  /** Activate two-thumb range selection */
  range?: boolean;
  /** Component size */
  size?: ComponentSize;
  /** Step increment */
  step?: number | string;
  /** Range mode: upper bound */
  to?: number | string;
  /** Range mode a11y label for the end thumb (e.g. "$80") */
  'to-value-text'?: string;
  /** Single-value mode: current value */
  value?: number | string;
  /** Single-value mode a11y label override (e.g. "75%"). Overrides raw aria-valuenow. */
  'value-text'?: string;
};

/**
 * A slider for selecting a single numeric value or a numeric range.
 *
 * Add the boolean `range` attribute to activate two-thumb range mode.
 *
 * @element ore-slider
 *
 * @attr {number}  min   - Minimum value (default: 0)
 * @attr {number}  max   - Maximum value (default: 100)
 * @attr {number}  step  - Step increment (default: 1)
 * @attr {number}  value - Current value (single mode)
 * @attr {number}  from  - Lower bound (range mode)
 * @attr {number}  to    - Upper bound (range mode)
 * @attr {boolean} range - Activate range mode
 * @attr {boolean} disabled - Disable interaction
 * @attr {string}  name  - Form field name (single mode)
 * @attr {string}  color - Theme color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
 * @attr {string}  size  - 'sm' | 'md' | 'lg'
 * @attr {string}  helper - Helper text below the slider
 * @attr {string}  error  - Error message below the slider
 * @attr {boolean} fullwidth - Stretch to full width of container
 *
 * @fires input - Emitted when the slider value changes.
 * @fires change - Emitted when the slider value changes.
 *
 * @slot - Slider label text
 *
 * @part slider      - Slider container
 * @part track       - Track element
 * @part fill        - Fill element
 * @part thumb       - Single-value thumb
 * @part thumb-start - Range start thumb
 * @part thumb-end   - Range end thumb
 * @part label       - Label element
 * @part helper-text - The helper/error text element
 *
 * @cssprop --slider-height    - Track height
 * @cssprop --slider-size      - Thumb dimensions
 * @cssprop --slider-track-bg  - Track background color
 * @cssprop --slider-fill      - Active fill color
 * @cssprop --slider-thumb-bg  - Thumb background color
 *
 * @example
 * ```html
 * <ore-slider value="50" name="volume">Volume</ore-slider>
 * <ore-slider range from="20" to="80" color="primary">Price range</ore-slider>
 * ```
 */
export const SLIDER_TAG = 'ore-slider' as const;
define<OreSliderProps>(SLIDER_TAG, {
  formAssociated: true,
  props: {
    ...themableBundle,
    ...sizableBundle,
    ...disablableBundle,
    error: prop.string(),
    from: prop.number(0),
    'from-value-text': prop.string(),
    fullwidth: prop.bool(false),
    helper: prop.string(),
    max: prop.number(100),
    min: prop.number(0),
    name: prop.string(),
    range: prop.bool(false),
    step: prop.number(1),
    to: prop.number(100),
    'to-value-text': prop.string(),
    value: prop.number(0),
    'value-text': prop.string(),
  },
  setup(props) {
    const el = getHost();
    const slots = useSlots();

    // Treat `range` as static — determined at first render
    const isRange = props.range.value;
    // ── Shared helpers ────────────────────────────────────────────
    const sliderControl = createSliderControl({
      max: props.max,
      min: props.min,
      step: props.step,
    });
    // ── Single-value state ────────────────────────────────────────
    const isDragging = signal(false);
    const isDisabled = computed(() => Boolean(props.disabled.value));
    const labelledById = signal<string | undefined>(undefined);
    const assistiveId = createStableId('helper');
    const assistive = createErrorHelperState({ error: props.error, helper: props.helper });
    const errorText = computed(() => assistive.value.errorText);
    const helperText = computed(() => assistive.value.helperText);
    const ariaDescribedBy = computed(() => (errorText.value || helperText.value ? assistiveId : null));

    bind({
      attr: {
        'data-dragging': () => (isDragging.value ? true : undefined),
        size: props.size,
      },
    });

    const valueSignal = signal('0');

    if (!isRange) {
      useField({ disabled: isDisabled, value: valueSignal });
      watch(
        props.value,
        (v) => {
          valueSignal.value = String(v);
        },
        { immediate: true },
      );
      bind({
        attr: {
          ariaDescribedby: () => ariaDescribedBy.value ?? null,
          ariaDisabled: () => (isDisabled.value ? 'true' : null),
          ariaLabelledby: () => labelledById.value ?? null,
          ariaValuemax: () => sliderControl.max(),
          ariaValuemin: () => sliderControl.min(),
          ariaValuenow: () => Number(valueSignal.value || 0),
          ariaValuetext: () => props['value-text'].value ?? null,
          role: () => 'slider',
          tabindex: () => (isDisabled.value ? null : '0'),
        },
      });
    }

    // ── Range state ───────────────────────────────────────────────
    const startVal = signal(0);
    const endVal = signal(100);

    defineFieldValue(
      el,
      () => (isRange ? `${startVal.value},${endVal.value}` : valueSignal.value),
      (value) => {
        if (isRange) {
          const [from, to] = value.split(',').map(Number);

          if (Number.isFinite(from)) startVal.value = sliderControl.snap(from);

          if (Number.isFinite(to)) endVal.value = sliderControl.snap(to);

          return;
        }

        const next = Number(value);

        if (Number.isFinite(next)) valueSignal.value = String(sliderControl.snap(next));
      },
    );

    if (isRange) {
      useField<{
        from: number;
        to: number;
      }>({
        disabled: isDisabled,
        toFormValue: ({ from, to }) => {
          const name = props.name.value;

          if (!name) return null;

          const fd = new FormData();

          fd.append(`${name}[from]`, String(from));
          fd.append(`${name}[to]`, String(to));

          return fd;
        },
        value: computed(() => ({ from: startVal.value, to: endVal.value })),
      });
      watch(
        props.from,
        (v) => {
          startVal.value = sliderControl.snap(Number(v));
        },
        { immediate: true },
      );
      watch(
        props.to,
        (v) => {
          endVal.value = sliderControl.snap(Number(v));
        },
        { immediate: true },
      );
    }

    // ── Refs ──────────────────────────────────────────────────────
    const containerRef = ref<HTMLDivElement>();
    const labelRef = ref<HTMLSpanElement>();
    const trackRef = ref<HTMLDivElement>();
    const thumbStartRef = ref<HTMLDivElement>();
    const thumbEndRef = ref<HTMLDivElement>();
    const startId = createStableId('slider-start');
    const endId = createStableId('slider-end');
    // ── CSS update helpers ────────────────────────────────────────
    const updateSingleCSS = (value: number) => {
      const pct = sliderControl.toPercent(value);

      el.style.setProperty('--_thumb-pos', `${pct}%`);
      el.style.setProperty('--_fill-start', '0%');
      el.style.setProperty('--_fill-width', `${pct}%`);
    };
    const updateRangeCSS = () => {
      const s = sliderControl.toPercent(startVal.value);
      const e = sliderControl.toPercent(endVal.value);

      el.style.setProperty('--_thumb-start', `${s}%`);
      el.style.setProperty('--_thumb-end', `${e}%`);
      el.style.setProperty('--_fill-start', `${s}%`);
      el.style.setProperty('--_fill-width', `${e - s}%`);
    };
    const emitValueChange = () => {
      const value = isRange ? `${startVal.value},${endVal.value}` : valueSignal.value;

      setFieldValue(el, value);
      dispatchNativeFieldEvent(el, 'input');
      dispatchNativeFieldEvent(el, 'change');
    };

    // ── Range mode setup ──────────────────────────────────────────
    const setupRangeMode = (container: HTMLDivElement, track: HTMLDivElement) => {
      updateRangeCSS();

      const clientToValue = (clientX: number) => {
        const rect = track.getBoundingClientRect();

        return sliderControl.fromClientX(clientX, rect);
      };
      let dragging: 'start' | 'end' | null = null;
      const applyDrag = (val: number) => {
        if (dragging === 'start') startVal.value = Math.min(sliderControl.snap(val), endVal.value);
        else if (dragging === 'end') endVal.value = Math.max(sliderControl.snap(val), startVal.value);

        startVal.value = sliderControl.clamp(startVal.value);
        endVal.value = sliderControl.clamp(endVal.value);
        updateRangeCSS();
        emitValueChange();
      };

      onEvent(
        container,
        'pointerdown',
        guard(
          () => !isDisabled.value,
          (e: PointerEvent) => {
            e.preventDefault();

            const val = clientToValue(e.clientX);

            dragging = Math.abs(val - startVal.value) <= Math.abs(val - endVal.value) ? 'start' : 'end';
            (e.target as Element).setPointerCapture(e.pointerId);
            applyDrag(val);
          },
        ),
      );
      onEvent(
        container,
        'pointermove',
        guard(
          () => !!dragging,
          (e: PointerEvent) => {
            e.preventDefault();

            if (!isDragging.value) isDragging.value = true;

            applyDrag(clientToValue(e.clientX));
          },
        ),
      );
      onEvent(
        container,
        'pointerup',
        guard(
          () => !!dragging,
          (e: PointerEvent) => {
            e.preventDefault();
            dragging = null;
            isDragging.value = false;
            (e.target as Element).releasePointerCapture(e.pointerId);
          },
        ),
      );

      const makeThumbKeydown = (getVal: () => number, setVal: (v: number) => void) => (e: KeyboardEvent) => {
        if (isDisabled.value) return;

        const next = sliderControl.nextFromKey(e.key, getVal());

        if (next === null) return;

        e.preventDefault();
        setVal(sliderControl.snap(next));
        updateRangeCSS();
        emitValueChange();
      };
      const thumbStartEl = thumbStartRef.value;
      const thumbEndEl = thumbEndRef.value;

      if (thumbStartEl) {
        onEvent(
          thumbStartEl,
          'keydown',
          makeThumbKeydown(
            () => startVal.value,
            (v) => {
              startVal.value = Math.min(v, endVal.value);
            },
          ),
        );
        onCleanup(
          bind(
            {
              aria: {
                label: 'Range start',
                valuemax: () => endVal.value,
                valuemin: () => sliderControl.min(),
                valuenow: () => startVal.value,
                valuetext: () => props['from-value-text'].value ?? null,
              },
            },
            { target: thumbStartEl },
          ),
        );
      }

      if (thumbEndEl) {
        onEvent(
          thumbEndEl,
          'keydown',
          makeThumbKeydown(
            () => endVal.value,
            (v) => {
              endVal.value = Math.max(v, startVal.value);
            },
          ),
        );
        onCleanup(
          bind(
            {
              aria: {
                label: 'Range end',
                valuemax: () => sliderControl.max(),
                valuemin: () => startVal.value,
                valuenow: () => endVal.value,
                valuetext: () => props['to-value-text'].value ?? null,
              },
            },
            { target: thumbEndEl },
          ),
        );
      }
    };
    // ── Single-value mode setup ───────────────────────────────────
    const setupSingleMode = (container: HTMLDivElement, track: HTMLDivElement) => {
      updateSingleCSS(Number(valueSignal.value));

      const updateValue = (clientX: number) => {
        if (isDisabled.value) return;

        const rect = track.getBoundingClientRect();
        const newValue = sliderControl.fromClientX(clientX, rect);

        if (Number(valueSignal.value) !== newValue) {
          valueSignal.value = newValue.toString();
          updateSingleCSS(newValue);
          emitValueChange();
        }
      };
      let isPointerDragging = false;

      onEvent(
        container,
        'pointerdown',
        guard(
          () => !isDisabled.value,
          (e: PointerEvent) => {
            e.preventDefault();
            isPointerDragging = true;
            updateValue(e.clientX);
            (e.target as Element).setPointerCapture(e.pointerId);
          },
        ),
      );
      onEvent(
        container,
        'pointermove',
        guard(
          () => isPointerDragging,
          (e: PointerEvent) => {
            e.preventDefault();

            if (!isDragging.value) isDragging.value = true;

            updateValue(e.clientX);
          },
        ),
      );
      onEvent(
        container,
        'pointerup',
        guard(
          () => isPointerDragging,
          (e: PointerEvent) => {
            e.preventDefault();
            isPointerDragging = false;
            isDragging.value = false;
            (e.target as Element).releasePointerCapture(e.pointerId);
          },
        ),
      );
      onEvent(
        el,
        'keydown',
        guard(
          () => !isDisabled.value,
          (e: KeyboardEvent) => {
            const val = Number(valueSignal.value || 0);
            const next = sliderControl.nextFromKey(e.key, val);

            if (next === null) return;

            e.preventDefault();

            const newValue = sliderControl.clamp(next);

            if (newValue !== val) {
              valueSignal.value = newValue.toString();
              updateSingleCSS(newValue);
              emitValueChange();
            }
          },
        ),
      );
    };

    onMounted(() => {
      const container = containerRef.value;
      const track = trackRef.value;

      if (!container || !track) return;

      if (slots.has().value && labelRef.value) {
        const labelId = createStableId('slider-label');

        labelRef.value.id = labelId;

        if (!isRange) labelledById.value = labelId;
      }

      if (isRange) setupRangeMode(container, track);
      else setupSingleMode(container, track);
    });

    return html`
      <div class="slider-container" part="slider" ref=${containerRef}>
        <div class="slider-track" part="track" ref=${trackRef}>
          <div class="slider-fill" part="fill"></div>
          <div class="slider-thumb slider-thumb-sole" part="thumb"></div>
          <div
            class="slider-thumb slider-thumb-start"
            part="thumb-start"
            ref=${thumbStartRef}
            role="slider"
            tabindex="${() => (isDisabled.value ? '-1' : '0')}"
            aria-describedby="${ariaDescribedBy}"
            id="${startId}"></div>
          <div
            class="slider-thumb slider-thumb-end"
            part="thumb-end"
            ref=${thumbEndRef}
            role="slider"
            tabindex="${() => (isDisabled.value ? '-1' : '0')}"
            aria-describedby="${ariaDescribedBy}"
            id="${endId}"></div>
        </div>
      </div>
      <span class="label" part="label" ref=${labelRef}><slot></slot></span>
      ${renderHelperRegion(assistiveId, errorText, helperText)}
    `;
  },
  styles: [
    disabledStateMixin,
    colorThemeMixin,
    sizeVariantMixin(SLIDER_SIZE_PRESET),
    componentStyles,
    reducedMotionMixin,
  ],
});
